import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
	TSmartVariableDefinition,
	TFilterGroup,
	TFilterCondition,
	TFilterView,
} from '@/types/TSmartCRM';
import { EFilterLogic, EFilterOperator } from 'src/enums/ESmartCRM';
import { FIELD_TYPE_CONFIGS } from 'src/constants/smartCRM';
import {
	convertBackendToFilterGroups,
	convertFilterGroupsForBackend,
	normalizeDate,
} from '@/utils/SmartCRM/filterUtils';

interface UseFilterBuilderProps {
	fields: TSmartVariableDefinition[];
	activeFilterView?: TFilterView | null;
	onFiltersChange?: (filters: TFilterGroup[]) => void;
	onSaveAsView?: (name: string, filters: any, viewId?: string) => void;
	onSwitchToCustomers?: () => void;
}

export const useFilterBuilder = ({
	fields,
	activeFilterView,
	onFiltersChange,
	onSaveAsView,
	onSwitchToCustomers,
}: UseFilterBuilderProps) => {
	const [filterGroups, setFilterGroups] = useState<TFilterGroup[]>([]);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [viewName, setViewName] = useState('');
	const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
	const [editingGroupName, setEditingGroupName] = useState('');

	const isEditingExistingView = !!activeFilterView;
	const hasValidFilters = filterGroups.some((group) => {
		const hasValid = group.conditions.some(
			(c) => c.field && c.operator && c.value !== null && c.value !== '' && c.value !== undefined,
		);
		return hasValid;
	});

	useEffect(() => {
		if (activeFilterView?.filterGroups) {
			const convertedGroups = convertBackendToFilterGroups(activeFilterView.filterGroups, fields);
			setFilterGroups(convertedGroups);
			setViewName(activeFilterView.name);
		} else {
			setFilterGroups([]);
			setViewName('');
		}
	}, [activeFilterView, fields]);

	const updateFilters = useCallback(
		(updatedGroups: TFilterGroup[]) => {
			setFilterGroups(updatedGroups);
			onFiltersChange?.(updatedGroups);
		},
		[onFiltersChange],
	);

	const addFilterGroup = useCallback(() => {
		const newGroup: TFilterGroup = {
			id: `temp_group_${Date.now()}`,
			name: `Filter Group ${filterGroups.length + 1}`,
			logic: EFilterLogic.AND,
			conditions: [],
		};
		updateFilters([...filterGroups, newGroup]);
	}, [filterGroups, updateFilters]);

	const removeGroup = useCallback(
		(groupId: string) => {
			updateFilters(filterGroups.filter((group) => group.id !== groupId));
		},
		[filterGroups, updateFilters],
	);

	const addCondition = useCallback(
		(groupId: string) => {
			const newCondition: TFilterCondition = {
				id: `temp_condition_${Date.now()}`,
				field: '',
				operator: EFilterOperator.EQUALS,
				value: null,
			};

			updateFilters(
				filterGroups.map((group) =>
					group.id === groupId
						? { ...group, conditions: [...group.conditions, newCondition] }
						: group,
				),
			);
		},
		[filterGroups, updateFilters],
	);

	const removeCondition = useCallback(
		(groupId: string, conditionId: string) => {
			updateFilters(
				filterGroups.map((group) =>
					group.id === groupId
						? { ...group, conditions: group.conditions.filter((c) => c.id !== conditionId) }
						: group,
				),
			);
		},
		[filterGroups, updateFilters],
	);

	const updateCondition = useCallback(
		(groupId: string, conditionId: string, updates: Partial<TFilterCondition>) => {
			updateFilters(
				filterGroups.map((group) =>
					group.id === groupId
						? {
								...group,
								conditions: group.conditions.map((c) => {
									if (c.id === conditionId) {
										const updatedCondition = { ...c, ...updates };
										if (updates.field) {
											const fieldDef = fields.find((f) => f.internalKey === updates.field);
											if (fieldDef && FIELD_TYPE_CONFIGS[fieldDef.fieldType]) {
												const supportedOperators = FIELD_TYPE_CONFIGS[fieldDef.fieldType].operators;
												if (!supportedOperators.includes(updatedCondition.operator)) {
													updatedCondition.operator = supportedOperators[0];
												}
											}
										}
										if (updates.value !== undefined) {
											const fieldDef = fields.find((f) => f.internalKey === c.field);
											if (fieldDef?.fieldType === 'Date') {
												updatedCondition.value = normalizeDate(updates.value);
											} else {
												updatedCondition.value = updates.value;
											}
										}
										return updatedCondition;
									}
									return c;
								}),
							}
						: group,
				),
			);
		},
		[filterGroups, fields, updateFilters],
	);

	const updateGroupName = useCallback(
		(groupId: string, name: string) => {
			updateFilters(
				filterGroups.map((group) => (group.id === groupId ? { ...group, name } : group)),
			);
		},
		[filterGroups, updateFilters],
	);

	const handleSaveOrUpdate = useCallback(() => {
		if (!hasValidFilters) {
			message.error('Please add at least one valid filter condition');
			return;
		}

		const backendFilterGroups = convertFilterGroupsForBackend(filterGroups, fields);
		const processedGroups = {};

		backendFilterGroups.forEach((group, index) => {
			const originalGroup = filterGroups[index];
			const groupKey = originalGroup?.id || `temp_group_${Date.now()}_${index}`;
			processedGroups[groupKey] = group;
		});

		if (isEditingExistingView && activeFilterView) {
			onSaveAsView?.(activeFilterView.name, processedGroups, activeFilterView.id);
		} else {
			setShowSaveModal(true);
		}
	}, [
		hasValidFilters,
		filterGroups,
		fields,
		isEditingExistingView,
		activeFilterView,
		onSaveAsView,
	]);

	const handleNewViewSave = useCallback(() => {
		if (!viewName.trim()) {
			message.error('Please enter a view name');
			return;
		}
		if (!hasValidFilters) {
			message.error('Please add at least one valid filter condition');
			return;
		}

		const backendFilterGroups = convertFilterGroupsForBackend(filterGroups, fields);
		const processedGroups = {};

		backendFilterGroups.forEach((group, index) => {
			const groupKey = `temp_group_${Date.now()}_${index}`;
			processedGroups[groupKey] = group;
		});

		onSaveAsView?.(viewName.trim(), processedGroups);
		onSwitchToCustomers?.();
		setViewName('');
		setShowSaveModal(false);
	}, [viewName, hasValidFilters, filterGroups, fields, onSaveAsView, onSwitchToCustomers]);

	return {
		filterGroups,
		showSaveModal,
		viewName,
		editingGroupId,
		editingGroupName,
		isEditingExistingView,
		hasValidFilters,
		setViewName,
		setEditingGroupId,
		setEditingGroupName,
		setShowSaveModal,
		addFilterGroup,
		removeGroup,
		addCondition,
		removeCondition,
		updateCondition,
		updateGroupName,
		handleSaveOrUpdate,
		handleNewViewSave,
	};
};
